import { AlertTriangle, Clock, AlertCircle } from 'lucide-react';
import { formatDistanceToNow, parseISO, isPast, differenceInDays } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Helper function to format days into years/months/days
const formatDaysToYMD = (days) => {
  if (days < 0) return formatDaysToYMD(Math.abs(days));
  
  const years = Math.floor(days / 365);
  const remainingAfterYears = days % 365;
  const months = Math.floor(remainingAfterYears / 30);
  const remainingDays = remainingAfterYears % 30;
  
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (remainingDays > 0 || parts.length === 0) parts.push(`${remainingDays}d`);
  
  return parts.join(' ');
};

export const DocumentExpiryWarning = ({ expiryDate, documentName }) => {
  if (!expiryDate) {
    return null;
  }

  try {
    const expiry = parseISO(expiryDate);
    const now = new Date();
    const isExpired = isPast(expiry);
    const daysUntilExpiry = differenceInDays(expiry, now);
    const isExpiringSoon = daysUntilExpiry <= 60 && daysUntilExpiry > 0;

    if (isExpired) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 border border-red-300 cursor-help">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-xs font-medium text-red-700">Expired</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {documentName} expired{' '}
                {formatDistanceToNow(expiry, { addSuffix: true })}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (isExpiringSoon) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-100 border border-yellow-300 cursor-help">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <span className="text-xs font-medium text-yellow-700">
                  Expires {formatDaysToYMD(daysUntilExpiry)}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {documentName} expires{' '}
                {formatDistanceToNow(expiry, { addSuffix: true })}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 border border-blue-300 cursor-help">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">
                Expires {formatDaysToYMD(daysUntilExpiry)}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {documentName} expires{' '}
              {formatDistanceToNow(expiry, { addSuffix: true })}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  } catch (error) {
    console.error('Error parsing expiry date:', error);
    return null;
  }
};
